// Pure decision logic for provisioning a platform's Plivo-managed IP ACL into
// the customer's account at trunk-submit time. Separated from the sheet so the
// name-collision and IP-verification rules are unit-testable.

import type { IPAccessControlList } from 'v2/types/api/settings'
import {
  getManagedAclIps,
  normalizeAclIp,
  SIP_PLATFORMS,
  type SipPlatformKey,
} from './platform-config'

const MAX_NAME_ATTEMPTS = 5

/**
 * Page shape of the offset-paginated ACL list, structurally compatible with
 * settingsService.getIPAccessControlLists so the service method is passed in
 * as-is (and tests can enforce the API's page cap on a plain mock).
 */
export type AclListFetcher = (params: { limit: number; offset: number }) => Promise<{
  data?: {
    meta?: { total_count?: number; next?: string | null }
    objects?: IPAccessControlList[]
  }
}>

// The public plivo-api rejects limit > 20 ("Invalid limit N provided, limit
// should be between 0 to 20"), so 20 is the only legal page size.
export const ACL_PAGE_LIMIT = 20
/** Sanity cap on how many ACLs the lookup will walk (250 pages — the ceiling
 * the pre-fix code had, kept so the smaller page size does not shrink the
 * collision-detection coverage the #3524 review required). */
export const MAX_ACL_FETCH = 5000

/**
 * Fresh fetch of EVERY page of the account's ACLs at submit time: the endpoint
 * is offset-paginated, and a managed ACL (or a candidate-name collision) beyond
 * the first page would otherwise look absent and trigger a duplicate-name
 * create. The first page reports total_count, so the remaining pages are
 * fetched in parallel rather than sequentially.
 */
export const fetchAllAccountAcls = async (
  listAcls: AclListFetcher,
): Promise<IPAccessControlList[]> => {
  const firstPage = await listAcls({ limit: ACL_PAGE_LIMIT, offset: 0 })
  const accountAcls: IPAccessControlList[] = [...(firstPage.data?.objects ?? [])]
  const totalCount = Math.min(firstPage.data?.meta?.total_count ?? 0, MAX_ACL_FETCH)
  if (firstPage.data?.meta?.next && totalCount > ACL_PAGE_LIMIT) {
    const offsets = Array.from(
      { length: Math.ceil((totalCount - ACL_PAGE_LIMIT) / ACL_PAGE_LIMIT) },
      (_, i) => (i + 1) * ACL_PAGE_LIMIT,
    )
    const pages = await Promise.all(
      offsets.map((offset) => listAcls({ limit: ACL_PAGE_LIMIT, offset })),
    )
    for (const page of pages) {
      accountAcls.push(...(page.data?.objects ?? []))
    }
  }
  return accountAcls
}

export interface ManagedAclResolution {
  /** Set when an account ACL already carries the platform's IPs — reuse it */
  ipaclUuid?: string
  /** Set when no suitable ACL exists — create one with this payload */
  createPayload?: { name: string; ip_addresses: string[] }
  /** Set when every candidate name is taken by ACLs with foreign IPs */
  error?: string
}

/**
 * Reuse-or-create decision for the managed ACL. A name match alone is NOT
 * trusted: a customer may own an ACL literally named "vapi-acl" with their
 * office IPs — attaching it would silently authenticate the wrong network.
 * Reuse requires the platform's IP set to be present; on a name collision with
 * foreign IPs we step to a suffixed name instead of mutating the customer's ACL.
 */
export const resolveManagedAcl = (
  platform: SipPlatformKey,
  accountAcls: IPAccessControlList[],
): ManagedAclResolution => {
  const baseName = SIP_PLATFORMS[platform].managedAclName || ''
  const managedIps = getManagedAclIps(platform)
  if (!baseName || managedIps.length === 0) {
    return { error: 'Platform has no managed IP access control list' }
  }
  const byName = new Map(accountAcls.map((acl) => [acl.name, acl]))
  for (let attempt = 0; attempt < MAX_NAME_ATTEMPTS; attempt++) {
    const name = attempt === 0 ? baseName : `${baseName}-${attempt + 1}`
    const existing = byName.get(name)
    if (!existing) {
      return { createPayload: { name, ip_addresses: managedIps } }
    }
    const existingIps = new Set(existing.ip_addresses.map(normalizeAclIp))
    if (managedIps.every((ip) => existingIps.has(ip))) {
      return { ipaclUuid: existing.ipacl_uuid }
    }
  }
  return { error: `Could not find a free name for the ${baseName} IP access control list` }
}
